/**
 * Step4_ControlMeasures Component
 *
 * Fourth step of the wizard - Define control measures to reduce risk.
 *
 * @module components/Step4_ControlMeasures
 */

import React, { useState } from 'react';
import {
  AlertTriangle,
  Shield,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Ban,
  RefreshCw,
  Settings,
  FileText,
  ShieldAlert,
  BookOpen,
  Calendar,
  User
} from 'lucide-react';
import Dropdown from '../../../components/common/Dropdown';
import { RiskLevelBadge } from './RiskMatrix';
import { ControlLibrary, ControlHierarchyGuide } from './ControlLibrary';
import {
  getRiskLevel,
  getAlarpZone,
  LIKELIHOOD_SCALE,
  SEVERITY_SCALE,
  CONTROL_HIERARCHY
} from '../config/moduleConfig';

/**
 * Get background color for risk level
 */
const getRiskBgColor = (score) => {
  if (score >= 20) return 'bg-red-100';
  if (score >= 12) return 'bg-orange-100';
  if (score >= 6) return 'bg-yellow-100';
  return 'bg-green-100';
};

/**
 * Get control hierarchy icon
 */
const getControlIcon = (level) => {
  const icons = {
    elimination: Ban,
    substitution: RefreshCw,
    engineering: Settings,
    administrative: FileText,
    ppe: ShieldAlert
  };
  return icons[level] || Shield;
};

/**
 * Step4_ControlMeasures Component
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.setFormData - Function to update form data
 */
export const Step4_ControlMeasures = ({ formData, setFormData }) => {
  const [expandedHazard, setExpandedHazard] = useState(0);
  const [showControlLibrary, setShowControlLibrary] = useState(false);
  const [activeHazardForLibrary, setActiveHazardForLibrary] = useState(null);

  const hazards = formData.hazards || [];

  const updateHazard = (index, updates) => {
    setFormData(prev => ({
      ...prev,
      hazards: prev.hazards.map((hazard, i) =>
        i === index ? { ...hazard, ...updates } : hazard
      )
    }));
  };

  const toggleHazard = (index) => {
    setExpandedHazard(expandedHazard === index ? -1 : index);
  };

  const openControlLibrary = (index) => {
    setActiveHazardForLibrary(index);
    setShowControlLibrary(true);
  };

  const handleControlSelect = (control) => {
    if (activeHazardForLibrary !== null) {
      const hazard = hazards[activeHazardForLibrary];
      const currentControls = hazard.additionalControls || '';
      const newControls = currentControls
        ? `${currentControls}\n• ${control.text}`
        : `• ${control.text}`;

      updateHazard(activeHazardForLibrary, {
        additionalControls: newControls,
        controlHierarchyLevel: control.level
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Control Measures</h3>
        <p className="text-sm text-gray-600 mt-1">
          Define controls to reduce risk to ALARP (As Low As Reasonably Practicable)
        </p>
      </div>

      {/* Control Hierarchy Guide */}
      <ControlHierarchyGuide compact />

      {/* Risk Reduction Summary */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">Total Initial Risk</p>
          <p className="text-2xl font-bold text-gray-900">
            {hazards.reduce((sum, h) => sum + ((h.initialLikelihood || 1) * (h.initialSeverity || 1)), 0)}
          </p>
        </div>
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <p className="text-sm text-gray-600">Total Residual Risk</p>
          <p className="text-2xl font-bold text-green-700">
            {hazards.reduce((sum, h) => sum + ((h.residualLikelihood || 1) * (h.residualSeverity || 1)), 0)}
          </p>
        </div>
      </div>

      {/* Hazard Control Cards */}
      {hazards.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
          <Shield className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No hazards to control</p>
          <p className="text-sm text-gray-500 mt-1">
            Go back to Step 2 to add hazards
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {hazards.map((hazard, index) => {
            const initialScore = (hazard.initialLikelihood || 1) * (hazard.initialSeverity || 1);
            const residualScore = (hazard.residualLikelihood || 1) * (hazard.residualSeverity || 1);
            const isExpanded = expandedHazard === index;
            const riskReduction = initialScore - residualScore;
            const reductionPercent = Math.round((riskReduction / initialScore) * 100);
            const ControlIcon = getControlIcon(hazard.controlHierarchyLevel || 'ppe');

            return (
              <div
                key={hazard.id}
                className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden"
              >
                {/* Hazard Header */}
                <button
                  onClick={() => toggleHazard(index)}
                  className="w-full bg-slate-100 px-5 py-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle className={`h-5 w-5 ${
                      residualScore >= 20 ? 'text-red-500' :
                      residualScore >= 12 ? 'text-orange-500' :
                      residualScore >= 6 ? 'text-yellow-500' :
                      'text-green-500'
                    }`} />
                    <div>
                      <h4 className="font-medium text-gray-900">
                        Hazard {index + 1}: {hazard.hazardDescription?.substring(0, 40)}...
                      </h4>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <RiskLevelBadge score={initialScore} showLabel={false} />
                      <ArrowRight className="h-4 w-4 text-gray-400" />
                      <RiskLevelBadge score={residualScore} showLabel={false} />
                    </div>
                    {riskReduction > 0 && (
                      <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                        -{reductionPercent}%
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="p-6 space-y-6">
                    {/* Initial Risk Display */}
                    <div className={`p-3 rounded-lg ${getRiskBgColor(initialScore)}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Initial Risk Score</span>
                        <RiskLevelBadge score={initialScore} />
                      </div>
                    </div>

                    {/* Existing Controls */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Existing Controls (Already in Place)
                      </label>
                      <textarea
                        value={hazard.existingControls || ''}
                        onChange={e => updateHazard(index, { existingControls: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        placeholder="Describe controls currently in place..."
                      />
                    </div>

                    {/* Control Hierarchy Level */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Primary Control Type
                      </label>
                      <div className="grid grid-cols-5 gap-2">
                        {Object.entries(CONTROL_HIERARCHY).map(([key, control]) => {
                          const Icon = getControlIcon(key);
                          const isSelected = (hazard.controlHierarchyLevel || 'ppe') === key;
                          const colorClasses = {
                            elimination: 'border-green-500 bg-green-50 text-green-700',
                            substitution: 'border-blue-500 bg-blue-50 text-blue-700',
                            engineering: 'border-cyan-500 bg-cyan-50 text-cyan-700',
                            administrative: 'border-yellow-500 bg-yellow-50 text-yellow-700',
                            ppe: 'border-orange-500 bg-orange-50 text-orange-700'
                          };

                          return (
                            <button
                              key={key}
                              onClick={() => updateHazard(index, { controlHierarchyLevel: key })}
                              className={`p-3 rounded-lg border-2 text-center transition-all ${
                                isSelected
                                  ? colorClasses[key]
                                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              <Icon className="h-5 w-5 mx-auto mb-1" />
                              <p className="text-xs font-medium">{control.label}</p>
                            </button>
                          );
                        })}
                      </div>
                      <p className="mt-2 text-sm text-gray-500">
                        {CONTROL_HIERARCHY[hazard.controlHierarchyLevel || 'ppe']?.description}
                      </p>
                    </div>

                    {/* Additional Controls */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Additional Controls Required
                        </label>
                        <button
                          onClick={() => openControlLibrary(index)}
                          className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                        >
                          <BookOpen className="h-4 w-4" />
                          Browse Library
                        </button>
                      </div>
                      <textarea
                        value={hazard.additionalControls || ''}
                        onChange={e => updateHazard(index, { additionalControls: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        placeholder="Describe additional controls to be implemented..."
                      />
                    </div>

                    {/* Residual Risk Scoring */}
                    <div className="pt-4 border-t border-gray-200">
                      <h5 className="font-medium text-gray-900 mb-4">
                        Residual Risk (After Controls Applied)
                      </h5>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm text-gray-600 mb-2">Likelihood</label>
                          <Dropdown
                            value={hazard.residualLikelihood || 1}
                            onChange={(value) => updateHazard(index, { residualLikelihood: parseInt(value) })}
                            options={[1, 2, 3, 4, 5].map(val => ({
                              value: val,
                              label: `${val} - ${LIKELIHOOD_SCALE[val]?.label}`
                            }))}
                            width="w-full"
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-gray-600 mb-2">Severity</label>
                          <Dropdown
                            value={hazard.residualSeverity || 1}
                            onChange={(value) => updateHazard(index, { residualSeverity: parseInt(value) })}
                            options={[1, 2, 3, 4, 5].map(val => ({
                              value: val,
                              label: `${val} - ${SEVERITY_SCALE[val]?.label}`
                            }))}
                            width="w-full"
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-gray-600 mb-2">Residual Score</label>
                          <div className={`px-4 py-2 rounded-lg text-center ${getRiskBgColor(residualScore)}`}>
                            <span className="text-2xl font-bold">{residualScore}</span>
                            <span className="text-sm text-gray-600 ml-1">/ 25</span>
                          </div>
                        </div>
                      </div>

                      {/* Risk Reduction Display */}
                      {riskReduction > 0 && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-800">
                            <strong>Risk Reduction:</strong> {riskReduction} points ({reductionPercent}%)
                            <span className="text-gray-600 ml-2">
                              ({getRiskLevel(initialScore)} → {getRiskLevel(residualScore)})
                            </span>
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Action Plan (if ALARP or Intolerable) */}
                    {residualScore >= 6 && (
                      <div className={`p-4 rounded-lg border ${
                        residualScore >= 20
                          ? 'bg-red-50 border-red-200'
                          : 'bg-yellow-50 border-yellow-200'
                      }`}>
                        <h5 className={`font-medium mb-3 ${
                          residualScore >= 20 ? 'text-red-900' : 'text-yellow-900'
                        }`}>
                          Action Plan Required {residualScore >= 20 ? '(Intolerable Risk)' : '(ALARP Zone)'}
                        </h5>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Actions Required
                            </label>
                            <textarea
                              value={hazard.actionRequired || ''}
                              onChange={e => updateHazard(index, { actionRequired: e.target.value })}
                              rows={2}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                              placeholder="What actions will reduce this risk further?"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <User className="h-4 w-4 inline mr-1" />
                                Action Owner
                              </label>
                              <input
                                type="text"
                                value={hazard.actionOwner || ''}
                                onChange={e => updateHazard(index, { actionOwner: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                                placeholder="Who is responsible?"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Calendar className="h-4 w-4 inline mr-1" />
                                Deadline
                              </label>
                              <input
                                type="date"
                                value={hazard.actionDeadline || ''}
                                onChange={e => updateHazard(index, { actionDeadline: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Control Library Modal */}
      <ControlLibrary
        isOpen={showControlLibrary}
        onClose={() => setShowControlLibrary(false)}
        onSelect={handleControlSelect}
        selectedControls={[]}
      />
    </div>
  );
};

export default Step4_ControlMeasures;
